import React, { useMemo } from "react";
import {
  Box,
  Chip,
  Dialog,
  DialogContent,
  IconButton,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import FlightTakeoffIcon from "@mui/icons-material/FlightTakeoff";
import TrainIcon from "@mui/icons-material/Train";
import LocationCityIcon from "@mui/icons-material/LocationCity";
import VpnKeyIcon from "@mui/icons-material/VpnKey";
import KeyboardReturnIcon from "@mui/icons-material/KeyboardReturn";
import { useTranslation } from "react-i18next";

import { Location } from "../../../@types/DTO/Locations";
import { buildOpeningsSummary } from "./openingsSummary";
import {
  formatLocationAddress,
  isTruthyLocationFlag,
} from "./locationFormatters";

export interface LocationDetailsDialogProps {
  open: boolean;
  onClose: () => void;
  location: Location | null;
  anchorDate?: Date | null;
}

const LocationDetailsDialog: React.FC<LocationDetailsDialogProps> = ({
  open,
  onClose,
  location,
  anchorDate,
}) => {
  const { t, i18n } = useTranslation();

  const infoHtml = useMemo(() => {
    if (!location) return "";
    const lang = (i18n.language || "it").toLowerCase();
    const isEnglish = lang.startsWith("en");
    return (
      (isEnglish ? location.locationInfoEN : location.locationInfoLocal) || ""
    );
  }, [location, i18n.language]);

  const hoursText = useMemo(
    () => (location ? buildOpeningsSummary(location, { t, anchorDate }) : ""),
    [location, t, anchorDate]
  );

  // M-4: flag fields can arrive as the literal string "false"; the naive
  // Boolean(...) coercion treats that as truthy. Use the shared helper.
  const isAirport = isTruthyLocationFlag(location?.isAirport);
  const isRailway = isTruthyLocationFlag(location?.isRailway);
  const showPickupBadge = isTruthyLocationFlag(location?.pickUpOutOfHours);
  const showReturnBadge = isTruthyLocationFlag(location?.dropOffOutOfHours);

  const typeIconNode = isAirport ? (
    <FlightTakeoffIcon htmlColor="blue" />
  ) : isRailway ? (
    <TrainIcon htmlColor="green" />
  ) : (
    <LocationCityIcon color="primary" />
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      scroll="paper"
      sx={{
        "& .MuiDialog-container": {
          alignItems: "flex-start",
        },
        "& .MuiBackdrop-root": {
          backgroundColor: "transparent",
          backdropFilter: "blur(6px)",
          WebkitBackdropFilter: "blur(6px)",
        },
      }}
      PaperProps={{
        sx: {
          borderRadius: 2,
          mt: 6,
          maxHeight: "calc(100vh - 96px)",
        },
      }}
      disableAutoFocus
      disableEnforceFocus
      disableRestoreFocus
      aria-modal="true"
    >
      <DialogContent sx={{ p: 3 }}>
        {location && (
          <>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                flexWrap: "wrap",
                mb: 2,
              }}
            >
              {typeIconNode}
              <Box sx={{ flex: 1 }} />
              {showPickupBadge && (
                <Chip
                  size="small"
                  icon={<VpnKeyIcon />}
                  label={t("Ritiro 24 ore")}
                  variant="outlined"
                  color="primary"
                  sx={{ borderRadius: 999 }}
                />
              )}
              {showReturnBadge && (
                <Chip
                  size="small"
                  icon={<KeyboardReturnIcon />}
                  label={t("Restituzione 24 ore")}
                  variant="outlined"
                  color="primary"
                  sx={{ borderRadius: 999 }}
                />
              )}
              <IconButton
                size="small"
                onClick={onClose}
                aria-label={t("Close")}
                sx={{ ml: 1 }}
              >
                <CloseIcon />
              </IconButton>
            </Box>

            {/* Row 2: station name */}
            <Typography
              variant="h5"
              sx={{ fontWeight: 700, lineHeight: 1.2, mb: 0.5 }}
            >
              {location.locationName}
            </Typography>

            {/* Row 3: address */}
            <Typography variant="body2" sx={{ color: "text.secondary", mb: 3 }}>
              {formatLocationAddress(location)}
            </Typography>

            {/* Section: opening hours */}
            {hoursText && (
              <Box sx={{ mb: 3 }}>
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: 700, mb: 0.5 }}
                >
                  {t("Opening hours")}
                </Typography>
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  {hoursText}
                </Typography>
              </Box>
            )}

            {infoHtml && (
              <Box>
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: 700, mb: 0.5 }}
                >
                  {t("Station information")}
                </Typography>
                <Box
                  sx={{
                    "& p, & div": { m: 0, mb: 1 },
                    fontSize: "0.9rem",
                    color: "text.secondary",
                  }}
                  dangerouslySetInnerHTML={{ __html: infoHtml }}
                />
              </Box>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default LocationDetailsDialog;
